import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { PssAuthService } from '../../services/pss-auth.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent {
  form = this.fb.group({
    email:    ['', [Validators.required, Validators.email]],
    password: ['', Validators.required],
  });
  loading  = false;
  error    = '';
  debugLog: string[] = [];

  constructor(
    private fb: FormBuilder,
    private auth: PssAuthService,
    private http: HttpClient,
    private router: Router,
  ) {}

  log(msg: string) {
    const ts = new Date().toISOString().substring(11, 23);
    this.debugLog.push(`[${ts}] ${msg}`);
  }

  // Builds a message with enough context (which PSS call failed, PSS's own
  // errorCode, HTTP status) to be useful when a user pastes it into a bug report.
  // A plain Error from the browser-direct login path carries these as properties
  // on itself; an HttpErrorResponse from the /api/login proxy fallback carries
  // them inside err.error (the JSON body set by handleLogin).
  private describeLoginError(err: any): string {
    const body = err?.error && typeof err.error === 'object' ? err.error : null;
    const message   = body?.error ?? err?.message ?? 'Login failed.';
    const step      = body?.step ?? err?.step ?? null;
    const errorCode = body?.errorCode ?? err?.errorCode ?? null;
    const status    = err?.status ?? null;

    const context = [
      step ? `step=${step}` : null,
      errorCode ? `errorCode=${errorCode}` : null,
      status ? `httpStatus=${status}` : null,
    ].filter(Boolean);

    return context.length ? `${message} (${context.join(', ')})` : message;
  }

  testServer() {
    this.debugLog = [];
    this.log('Testing account server...');
    this.http.get('/accounts').subscribe({
      next: () => this.log('✓ Account server OK'),
      error: (e) => this.log('✗ Account server failed: ' + e.message)
    });

    this.log('Testing PSS API via proxy...');
    this.http.get(
      '/pss/api.pixelstarships.com/SettingService/getlatestversion3?languageKey=en&deviceType=DeviceTypeAndroid',
      { responseType: 'text' }
    ).subscribe({
      next:  (xml) => this.log('✓ PSS API OK: ' + xml.substring(0, 120)),
      error: (e)   => this.log('✗ PSS API failed: ' + e.message)
    });
  }

  submit() {
    if (this.form.invalid) {
      this.log('Form invalid — check email/password fields');
      console.warn('[login] form invalid', this.form.errors, this.form.get('email')?.errors, this.form.get('password')?.errors);
      return;
    }
    this.debugLog = [];
    this.loading  = true;
    this.error    = '';
    const { email, password } = this.form.value;

    this.log('Posting to /api/login...');
    this.auth.login(email!, password!).subscribe({
      next: (session) => {
        this.log('✓ Login OK — ' + session.name);
        this.router.navigate(['/dashboard']).then(() => {
          window.scrollTo(0, 0);
        });
      },
      error: (err) => {
        const detail = this.describeLoginError(err);
        this.log('✗ ' + detail);
        // Raw PSS response (browser-direct errors carry it on err.xml, proxy
        // errors inside err.error.xml). Truncated so a screenshot is enough.
        const rawXml = err?.xml ?? err?.error?.xml;
        if (rawXml) this.log('PSS raw: ' + String(rawXml).slice(0, 300));
        console.error('[login] error:', err);
        this.error  = detail;
        this.loading = false;
      }
    });
  }

  back() { this.router.navigate(['/']); }
}
